import { useEffect, useRef } from "react";

const VERTEX_SHADER = `
  attribute vec2 a_position;
  varying vec2 v_uv;

  void main() {
    v_uv = a_position * 0.5 + 0.5;
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

const FRAGMENT_SHADER = `
  precision highp float;

  uniform sampler2D u_current;
  uniform sampler2D u_incoming;
  uniform float u_progress;
  uniform float u_phase;
  varying vec2 v_uv;

  float hash21(vec2 point) {
    point = fract(point * vec2(123.34, 456.21));
    point += dot(point, point + 45.32);
    return fract(point.x * point.y);
  }

  float valueNoise(vec2 point) {
    vec2 cell = floor(point);
    vec2 local = fract(point);
    vec2 smoothLocal = local * local * (3.0 - 2.0 * local);
    float bottom = mix(hash21(cell), hash21(cell + vec2(1.0, 0.0)), smoothLocal.x);
    float top = mix(hash21(cell + vec2(0.0, 1.0)), hash21(cell + vec2(1.0, 1.0)), smoothLocal.x);
    return mix(bottom, top, smoothLocal.y);
  }

  float fluidNoise(vec2 point) {
    float value = 0.0;
    value += valueNoise(point) * 0.56;
    point = point * 2.03 + 7.4;
    value += valueNoise(point) * 0.29;
    point = point * 2.01 + 13.7;
    value += valueNoise(point) * 0.15;
    return value;
  }

  vec3 logoGradient(float radius) {
    float t = max(radius, 0.0684236);
    vec3 color = vec3(1.0, 0.756863, 0.341176);
    color = mix(
      color,
      vec3(1.0, 0.517647, 0.0),
      clamp((t - 0.0684236) / (0.486436 - 0.0684236), 0.0, 1.0)
    );
    color = mix(
      color,
      vec3(1.0, 0.556863, 0.898039),
      clamp((t - 0.486436) / (0.771469 - 0.486436), 0.0, 1.0)
    );
    color = mix(
      color,
      vec3(0.835294, 0.34902, 1.0),
      clamp((t - 0.771469) / (1.0 - 0.771469), 0.0, 1.0)
    );
    return color;
  }

  void main() {
    vec2 uv = v_uv;
    float motion = u_progress * 1.18 + u_phase;
    vec2 radialSpace = vec2((uv.x - 0.5) * 0.824691, uv.y - 1.035);
    float radialDistance = length(radialSpace) / 1.115;
    float polarAngle = atan(radialSpace.x, -radialSpace.y);
    float flow = (fluidNoise(vec2(polarAngle * 0.72 + u_phase, motion)) - 0.5) * 0.034;
    flow += sin(polarAngle * 2.0 + u_phase * 1.35) * 0.008;
    flow += sin(polarAngle * 4.0 - u_phase * 0.7) * 0.0035;

    float boundary = u_progress * 0.78 + 0.15 + flow;
    float signedEdge = radialDistance - boundary;
    float incomingMask = 1.0 - smoothstep(-0.016, 0.016, signedEdge);

    float gradientDistance = abs(signedEdge + 0.018);
    float gradientAlpha = 1.0 - smoothstep(0.052, 0.235, gradientDistance);

    vec3 currentColor = texture2D(u_current, uv).rgb;
    vec3 incomingColor = texture2D(u_incoming, uv).rgb;
    vec3 resourceColor = mix(currentColor, incomingColor, incomingMask);

    float logoLocalY = clamp((signedEdge + 0.235) / 0.47, 0.0, 1.0);
    vec2 logoPoint = vec2(uv.x * 20.4, logoLocalY * 24.0);
    vec2 logoDelta = logoPoint - vec2(1.03153, 23.9673);
    vec2 logoRotated = vec2(
      logoDelta.x * 0.621715 - logoDelta.y * 0.783243,
      logoDelta.x * 0.783243 + logoDelta.y * 0.621715
    );
    float logoRadius = length(logoRotated / vec2(29.8988, 28.1808));
    vec3 gradientColor = logoGradient(logoRadius);

    vec3 color = mix(resourceColor, gradientColor, gradientAlpha);
    gl_FragColor = vec4(color, 1.0);
  }
`;

function createShader(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(message || "Unable to compile gallery shader");
  }
  return shader;
}

function createProgram(gl) {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
  const program = gl.createProgram();
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const message = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(message || "Unable to link gallery shader");
  }
  return program;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function createCoverSource(image, width, height) {
  const source = document.createElement("canvas");
  source.width = width;
  source.height = height;
  const context = source.getContext("2d", { alpha: false });
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
  const drawWidth = image.naturalWidth * scale;
  const drawHeight = image.naturalHeight * scale;
  context.drawImage(image, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
  return source;
}

function createTexture(gl, source, textureUnit) {
  const texture = gl.createTexture();
  gl.activeTexture(textureUnit);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
  return texture;
}

/**
 * 使用同一套径向流体擦除 shader 在两张图片之间过渡。
 *
 * @param {object} props 组件参数。
 * @param {string} props.currentSrc 当前图片地址。
 * @param {string} props.incomingSrc 下一张图片地址。
 * @param {number} props.index 用于生成不同流体相位的稳定序号。
 * @param {string} [props.className] Canvas 的样式类名。
 * @param {number} [props.refreshIndex] 暴露给调用方的刷新索引。
 * @param {number} [props.refreshStep] 暴露给调用方的刷新步骤。
 * @param {string|null} [props.readyEventName] Shader 就绪后冒泡的事件名；传 null 时不派发事件。
 * @param {boolean} [props.observeProgress] 是否仅在 CSS 进度变化时绘制，适合常驻的滚动时间线。
 */
export default function BuildGalleryShader({
  currentSrc,
  incomingSrc,
  index,
  className = "build-gallery-card__shader",
  refreshIndex = index,
  refreshStep,
  readyEventName = "build-gallery-shader-ready",
  observeProgress = false,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    canvas.dataset.shaderReady = "false";
    canvas.classList.remove("is-ready");

    const bounds = canvas.getBoundingClientRect();
    if (bounds.right < -160 || bounds.left > window.innerWidth + 160) return undefined;

    let disposed = false;
    let animationFrame = 0;
    let gl;
    let program;
    let buffer;
    let currentTexture;
    let incomingTexture;
    let progressObserver;

    Promise.all([loadImage(currentSrc), loadImage(incomingSrc)])
      .then(([currentImage, incomingImage]) => {
        if (disposed) return;

        const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
        const width = Math.max(1, Math.round(bounds.width * pixelRatio));
        const height = Math.max(1, Math.round(bounds.height * pixelRatio));
        canvas.width = width;
        canvas.height = height;

        gl = canvas.getContext("webgl", {
          alpha: true,
          antialias: false,
          depth: false,
          premultipliedAlpha: false,
          powerPreference: "high-performance",
        });
        if (!gl) return;

        program = createProgram(gl);
        gl.useProgram(program);

        buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(
          gl.ARRAY_BUFFER,
          new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
          gl.STATIC_DRAW,
        );

        const position = gl.getAttribLocation(program, "a_position");
        gl.enableVertexAttribArray(position);
        gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

        currentTexture = createTexture(gl, createCoverSource(currentImage, width, height), gl.TEXTURE0);
        incomingTexture = createTexture(gl, createCoverSource(incomingImage, width, height), gl.TEXTURE1);
        gl.uniform1i(gl.getUniformLocation(program, "u_current"), 0);
        gl.uniform1i(gl.getUniformLocation(program, "u_incoming"), 1);

        const progressLocation = gl.getUniformLocation(program, "u_progress");
        const phaseLocation = gl.getUniformLocation(program, "u_phase");
        gl.uniform1f(phaseLocation, index * 1.731 + 0.37);
        gl.viewport(0, 0, width, height);

        const render = () => {
          if (disposed) return;
          const parsedProgress = Number.parseFloat(canvas.style.getPropertyValue("--shader-progress"));
          const progress = Number.isFinite(parsedProgress) ? parsedProgress : -0.56;
          gl.uniform1f(progressLocation, progress);
          gl.drawArrays(gl.TRIANGLES, 0, 6);
          if (canvas.dataset.shaderReady !== "true") {
            canvas.dataset.shaderReady = "true";
            canvas.classList.add("is-ready");
            if (readyEventName) {
              canvas.dispatchEvent(new CustomEvent(readyEventName, { bubbles: true }));
            }
          }
          if (!observeProgress && progress < 1.515) animationFrame = requestAnimationFrame(render);
        };

        if (observeProgress) {
          progressObserver = new MutationObserver(() => {
            cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(render);
          });
          progressObserver.observe(canvas, { attributes: true, attributeFilter: ["style"] });
        }
        animationFrame = requestAnimationFrame(render);
      })
      .catch(() => {
        canvas.classList.remove("is-ready");
      });

    return () => {
      disposed = true;
      cancelAnimationFrame(animationFrame);
      progressObserver?.disconnect();
      canvas.classList.remove("is-ready");
      delete canvas.dataset.shaderReady;
      if (gl) {
        if (currentTexture) gl.deleteTexture(currentTexture);
        if (incomingTexture) gl.deleteTexture(incomingTexture);
        if (buffer) gl.deleteBuffer(buffer);
        if (program) gl.deleteProgram(program);
        gl.getExtension("WEBGL_lose_context")?.loseContext();
      }
    };
  }, [currentSrc, incomingSrc, index, observeProgress, readyEventName]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      data-refresh-index={refreshIndex}
      data-refresh-step={refreshStep}
      aria-hidden="true"
    />
  );
}
